/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useEffect, useContext } from 'react';
import { FormGroup, Input, Badge, Label, Button, Col, Progress } from 'reactstrap';
import Alert from 'react-s-alert';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';

// service imports
import AuthService from '../../services/AuthService';
import GeneralService from '../../services/GeneralService';

// local imports
import ErrorBoundary from './ErrorBoundary';
import GeneralNavbar from './GeneralNavbar';
import Message from './Message';
// import './Form.css';

const ProviderForm = props => {

    // contexts
    const { theme, colors } = useContext(ThemeContext);
    const { user, setUser } = useContext(AuthContext);

    // useState
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [formId, setFormId] = useState("");
    const [formName, setFormName] = useState("");
    const [formQuestions, setFormQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showPrevBtn, setShowPrevBtn] = useState(false);
    const [showNextBtn, setShowNextBtn] = useState(false);
    const [showSubmitBtn, setShowSubmitBtn] = useState(false);
    const [message, setMessage] = useState();

    useEffect(() => {
        const formId = props.match.params.id;
        setFormName("");
        setFormQuestions([]);
        setFormId(formId);
        openForm(formId);
    }, []);

    useEffect(() => {
        if (currentIndex === 0) {
            setShowPrevBtn(false);
        } else {
            setShowPrevBtn(true);
        }
        if (formQuestions.length === currentIndex + 1) {
            setShowNextBtn(false);
            setShowSubmitBtn(true);
        } else {
            setShowNextBtn(true);
            setShowSubmitBtn(false);
        }
    }, [currentIndex, formQuestions.length]);

    const openForm = (id) => {
        GeneralService.getProviderForm(id).then(form => {
            try {
                setIsSubmitted(false);
                setFormName(form.form);
                setFormQuestions(form.questions);
            } catch (err) {
                Alert.error("Some error occured, please try again later or report support team.", {
                    position: 'top-right',
                    effect: 'slide'
                });
                GeneralService.logError('at Client Form openForm', 'Error in try catch block', err.stack);
            }
        })
    }

    const validateAnswer = () => {
        if (formQuestions[currentIndex].type === "shortText" || formQuestions[currentIndex].type === "longText") {
            if (formQuestions[currentIndex].userResponse && formQuestions[currentIndex].userResponse.length && formQuestions[currentIndex].userResponse[0]) {
                return true;
            } else {
                setMessage({ msgError: true, msgBody: "Please type your answer" });
                return false;
            }
        } else {
            if (formQuestions[currentIndex].userResponse && formQuestions[currentIndex].userResponse.length) {
                return true;
            } else {
                setMessage({ msgError: true, msgBody: "Please select atleast one option" });
                return false;
            }
        }
    }

    const prevQuestion = () => {
        setCurrentIndex(currentIndex - 1);
    }

    const nextQuestion = () => {
        if (validateAnswer()) {
            setCurrentIndex(currentIndex + 1);
        }
    }

    const handleChange = (type, index) => {
        try {
            setMessage();
            if (!isSubmitted) {
                const tempArray = [...formQuestions];
                if (type === "textarea" || type === "text") {
                    tempArray[currentIndex].userResponse = [index];
                } else if (type === "radio") {
                    tempArray[currentIndex].userResponse = [formQuestions[currentIndex].answers[index]];
                } else {
                    const responseArray = tempArray[currentIndex].userResponse ? tempArray[currentIndex].userResponse : [];
                    const foundIndex = responseArray.indexOf(formQuestions[currentIndex].answers[index]);
                    if (foundIndex > -1) {
                        responseArray.splice(foundIndex, 1);
                    } else {
                        responseArray.push(formQuestions[currentIndex].answers[index]);
                    }
                    tempArray[currentIndex].userResponse = responseArray;
                }
                setFormQuestions(tempArray);
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client Form handleChange', 'Error in try catch block', err.stack);
        }
    }

    const handleSubmit = e => {
        try {
            if (validateAnswer()) {
                if (!isLoading) {
                    setIsLoading(true);
                    GeneralService.submitProviderForm(formId, formName, formQuestions).then(data => {
                        Alert.info(data.message.msgBody, {
                            position: 'top-right',
                            effect: 'slide'
                        });
                        setIsLoading(false);
                        props.history.push('/');
                    }).catch(error => {
                        Alert.error(error.message.msgBody, {
                            position: 'top-right',
                            effect: 'slide'
                        });
                        setIsLoading(false);
                    });
                }
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading(false);
            GeneralService.logError('at Client Form handleSubmit', 'Error in try catch block', err.stack);
        }
    }

    return (
        <div className="formDiv">
            <ErrorBoundary errorAt="At Provider ProviderForm Navbar">
                <GeneralNavbar history={props.history} />
            </ErrorBoundary>
            <div className="p-3 col-md-6 mt-5 offset-md-3" style={theme.form}>
                <ErrorBoundary errorAt="At Provider ProviderForm Message">
                    {message ? <Message message={message} /> : null}
                </ErrorBoundary>
                {
                    isLoading ?
                        <h4 className="text-center">Loading...</h4>
                        :
                        formQuestions.length > 0 ?
                            <div className="p-3 mt-3 questionCard">
                                <div className="d-flex">
                                    <Badge style={{ backgroundColor: colors.primary, marginRight: "15px", height: "fit-content" }}>{currentIndex + 1}</Badge>
                                    <span style={theme.headingBold}>{formQuestions[currentIndex].question}</span>
                                </div>
                                <hr />
                                <ErrorBoundary errorAt="At Provider ProviderForm FormAnswers">
                                    {
                                        formQuestions[currentIndex].type === "shortText" || formQuestions[currentIndex].type === 'email' || formQuestions[currentIndex].type === 'name' ?
                                            <FormGroup>
                                                <Input type="text" name={`answer${currentIndex}`} placeholder="Type your answer here..."
                                                    value={formQuestions[currentIndex].userResponse ? formQuestions[currentIndex].userResponse[0] : ''} onChange={(e) => handleChange(e.target.type, e.target.value)} />
                                            </FormGroup>
                                            :
                                            formQuestions[currentIndex].type === "longText" ?
                                                <FormGroup>
                                                    <Input type="textarea" name={`answer${currentIndex}`} placeholder="Type your answer here..." rows={5}
                                                        value={formQuestions[currentIndex].userResponse ? formQuestions[currentIndex].userResponse[0] : ''} onChange={(e) => handleChange(e.target.type, e.target.value)} />
                                                </FormGroup>
                                                :
                                                formQuestions[currentIndex].answers.map((item, index) => (
                                                    formQuestions[currentIndex].type === "radio" ?
                                                        <FormGroup key={index} tag="fieldset" row className="answerFormGroup">
                                                            <Col sm={10}>
                                                                <FormGroup check>
                                                                    <Label check className="answerLabel" style={{ cursor: "pointer" }}>
                                                                        <Input type="radio" name={`answer${currentIndex}`} style={{ cursor: "pointer" }}
                                                                            checked={formQuestions[currentIndex].userResponse ? formQuestions[currentIndex].userResponse[0] === item : false}
                                                                            onChange={(e) => handleChange(e.target.type, index)} />
                                                                        <span className="ml-2 radioText">{item}</span>
                                                                    </Label>
                                                                </FormGroup>
                                                            </Col>
                                                        </FormGroup>
                                                        :
                                                        formQuestions[currentIndex].type === "checkbox" ?
                                                            <FormGroup key={index} tag="fieldset" row className="answerFormGroup">
                                                                <Col sm={10}>
                                                                    <FormGroup check>
                                                                        <Label check className="answerLabel" style={{ cursor: "pointer" }}>
                                                                            <Input type="checkbox" name={`answer${currentIndex}`} style={{ cursor: "pointer" }}
                                                                                checked={formQuestions[currentIndex].userResponse ? formQuestions[currentIndex].userResponse.includes(item) : false}
                                                                                onChange={(e) => handleChange(e.target.type, index)} />
                                                                            <span className="ml-2 radioText">{item}</span>
                                                                        </Label>
                                                                    </FormGroup>
                                                                </Col>
                                                            </FormGroup>
                                                            : null
                                                ))
                                    }
                                </ErrorBoundary>
                                <div className="d-flex mt-3">
                                    {showPrevBtn ? <Button id="previousBtn" onClick={() => prevQuestion()}>Previous</Button> : null}
                                    {showNextBtn ? <Button id="nextBtn" className="ml-auto" style={{ backgroundColor: colors.primary }} onClick={() => nextQuestion()}>Next</Button> : null}
                                    {isSubmitted ? null : showSubmitBtn ? <Button id="submitBtn" className="ml-auto" style={{ backgroundColor: colors.primary }} onClick={() => handleSubmit()}>{isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Submit</Button> : null}
                                </div>
                            </div>
                            : null
                }
                <ErrorBoundary errorAt="At Provider ProviderForm ProgressBar">
                    {formQuestions.length > 0 ? <Progress value={(((currentIndex + 1) * 100) / formQuestions.length)} /> : null}
                </ErrorBoundary>
            </div>
        </div>
    )
}

export default ProviderForm;